import { goAnchor } from "./app-nav";
import { locale, t } from "@/lib/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, MoreHorizontal, Search, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import BroadcastPanel from "./broadcasts-panel";
import { BroadcastCalendar, BroadcastTable } from "./broadcasts-views";
import {
  broadcastChannels,
  broadcastEmpty,
  broadcastFolderNotes,
  broadcastFolders,
  broadcastHints,
  broadcastStatuses,
  demoBroadcasts,
  type Broadcast,
  type BroadcastStatus,
} from "@/mocks/broadcasts";

/* Живой макет окна «Рассылки»: папки по статусам, таблица и календарь отправок,
   пустое состояние с антенной и модальное окно создания. Демо-данные включаются внизу левой колонки. */

const RAIL = [
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: true },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: false },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: false },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: false },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: false },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels", active: false },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents", active: false },
  { id: "ai-assist", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
  { id: "lifecycle", icon: "ri-refresh-line", label: t("Жизненный цикл"), anchor: "#lifecycle", active: false },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: false },
];

const statusLabel = (id: string) => broadcastStatuses.find((s) => s.id === id)?.label ?? id;
const statusDot = (id: string) => broadcastStatuses.find((s) => s.id === id)?.dot ?? "bg-foreground/30";
const channelLabel = (id: string) => broadcastChannels.find((c) => c.id === id)?.label ?? id;

const fmt = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

function BroadcastDetails({ item, onClose }: { item: Broadcast; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = [
    { label: t("Статус"), value: statusLabel(item.status) },
    { label: t("Дата отправки"), value: fmt(item.date) },
    { label: t("Получатели"), value: item.audience > 0 ? item.audience.toLocaleString(locale) : t("аудитория не выбрана") },
    { label: t("Каналы"), value: item.channels.length > 0 ? item.channels.map(channelLabel).join(", ") : t("каналы не выбраны") },
    { label: t("Автор"), value: item.author },
    { label: t("Метки"), value: item.labels.length > 0 ? item.labels.join(", ") : t("без меток") },
  ];

  return (
    <div className="absolute inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" onClick={onClose} aria-label={t("Закрыть окно")} className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]" />
      <div role="dialog" aria-modal="true" aria-labelledby="broadcast-details-title" className="relative w-full max-w-[24rem] overflow-hidden rounded-card border border-border bg-card shadow-lift">
        <div className="flex items-start gap-2 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            <h4 id="broadcast-details-title" className="truncate text-[13.5px] font-semibold">
              {item.name}
            </h4>
            <p className="mt-0.5 flex items-center gap-1 text-[10.5px] text-muted-foreground">
              <span className={cn("size-1.5 rounded-full", statusDot(item.status))} aria-hidden />
              {statusLabel(item.status)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть окно")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <dl className="divide-y divide-border/60 px-4 py-1">
          {rows.map((r) => (
            <div key={r.label} className="flex items-start justify-between gap-3 py-2">
              <dt className="text-[11px] text-muted-foreground">{r.label}</dt>
              <dd className="max-w-[62%] text-end text-[11.5px] font-medium leading-relaxed text-foreground/85">{r.value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2.5">
          <p className="text-[10px] text-muted-foreground">{t("Демо-режим: отправка не выполняется")}</p>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn bg-primary px-4 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Понятно")}
          </button>
        </div>
      </div>
    </div>
  );
}

function BroadcastsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [folder, setFolder] = useState<"all" | BroadcastStatus>("all");
  const [view, setView] = useState<"table" | "calendar">("table");
  const [query, setQuery] = useState("");
  const [demo, setDemo] = useState(false);
  const [created, setCreated] = useState<Broadcast[]>([]);
  const [panel, setPanel] = useState(false);
  const [details, setDetails] = useState<Broadcast | null>(null);
  const [menu, setMenu] = useState(false);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const menuWrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => {
      if (menuWrap.current && !menuWrap.current.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const all = useMemo(() => {
    const base = demo ? demoBroadcasts : [];
    const overrides = new Map(created.map((b) => [b.id, b]));
    const merged = base.map((b) => overrides.get(b.id) ?? b);
    const extra = created.filter((b) => !base.some((d) => d.id === b.id));
    return [...merged, ...extra];
  }, [demo, created]);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: all.length };
    all.forEach((b) => {
      map[b.status] = (map[b.status] ?? 0) + 1;
    });
    return map;
  }, [all]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((b) => (folder === "all" ? true : b.status === folder))
      .filter((b) => (q ? b.name.toLowerCase().includes(q) || b.labels.some((l) => l.toLowerCase().includes(q)) : true))
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [all, folder, query]);

  const jump = (anchor: string, label: string) => {
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» в демо не открывается", { label }));
  };

  const create = (name: string, labels: string[]) => {
    const item: Broadcast = {
      id: `bc-new-${Date.now()}`,
      name,
      status: "draft",
      channels: [],
      audience: 0,
      date: "2026-10-29",
      author: t("Вы"),
      labels,
    };
    setCreated((prev) => [...prev, item]);
    setPanel(false);
    setFolder("draft");
    setView("table");
    setQuery("");
    flash(t("Черновик «{name}» создан — дальше выберите текст, аудиторию и каналы", { name }));
  };

  const toggleDemo = () => {
    const next = !demo;
    setDemo(next);
    if (!next) {
      setCreated((prev) => prev.filter((b) => !demoBroadcasts.some((d) => d.id === b.id)));
      setFolder("all");
    }
    flash(next ? t("Демо-данные включены: в окне девять рассылок во всех статусах") : t("Демо-данные выключены — окно снова пустое"));
  };

  const folderLabel = broadcastFolders.find((f) => f.id === folder)?.label ?? t("Все");

  return (
    <Band id="broadcasts" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска окна */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("рабочее пространство → рассылки")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className={cn("size-1.5 rounded-full", all.length > 0 ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden />
            {t("Рассылок: {n}", { n: all.length })}
          </span>
        </div>

        {/* пробный период */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary/[.08] px-3.5 py-2 text-[11.5px]">
          <i className="ri-information-line text-[13px] leading-none text-primary" aria-hidden />
          <span className="min-w-0 flex-1 leading-relaxed text-foreground/80">
            {t("Пробный период Growth Plan закончится 3 ноября 2026 — через 6 дней. Обновите тариф, чтобы не потерять доступ.")}
          </span>
          <button
            type="button"
            onClick={() => jump("#onboarding", t("Начало работы"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border border-primary/30 px-2.5 text-[10.5px] font-semibold text-primary transition hover:bg-primary/10"
          >
            {t("Чеклист онбординга")}
          </button>
          <button
            type="button"
            onClick={() => flash(t("Демо-режим: смена тарифа здесь недоступна"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-2.5 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Обновить тариф")}
          </button>
        </div>

        <div className="flex">
          {/* иконная панель */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => jump(r.anchor, r.label)}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {t("В")}
            </span>
          </div>

          {/* папки */}
          <div className="hidden w-[13.5rem] flex-none flex-col border-e border-border lg:flex">
            <div className="flex items-center gap-1 px-3 pb-1.5 pt-3">
              <p className="min-w-0 flex-1 truncate text-[13px] font-semibold">{t("Рассылки")}</p>
              <button
                type="button"
                onClick={() => searchRef.current?.focus()}
                aria-label={t("Найти рассылку")}
                title={t("Найти рассылку")}
                className="grid size-6 flex-none cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition hover:bg-muted hover:text-primary"
              >
                <Search className="size-3.5" aria-hidden />
              </button>
            </div>

            <div className="flex flex-col gap-0.5 px-2">
              {broadcastFolders.map((f) => {
                const active = folder === f.id;
                const count = counts[f.id] ?? 0;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFolder(f.id)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-[7px] text-start text-[12px] font-medium transition",
                      active ? "bg-primary/10 text-foreground" : "text-foreground/75 hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <span className={cn("size-2 flex-none rounded-full", f.dot)} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{f.label}</span>
                    {count > 0 ? <span className={cn("flex-none text-[10.5px] tabular-nums", active ? "text-primary" : "text-muted-foreground/80")}>{count}</span> : null}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 border-t border-border px-3 pt-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Папка")}</p>
              <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{broadcastFolderNotes[folder]}</p>
            </div>

            <div className="mt-auto border-t border-border p-2.5">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-[10.5px] font-semibold leading-tight text-muted-foreground">{t("Демо-данные")}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={demo}
                  aria-label={t("Демо-данные")}
                  onClick={toggleDemo}
                  className={cn("relative h-5 w-9 flex-none cursor-pointer rounded-full border transition", demo ? "border-primary bg-primary" : "border-border bg-foreground/10")}
                >
                  <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", demo ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
                </button>
              </div>
              <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                {demo ? t("Девять рассылок во всех статусах") : t("Включите, чтобы увидеть список и календарь")}
              </p>
            </div>
          </div>

          {/* основная область */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <h4 className="truncate text-[13px] font-semibold">{folderLabel}</h4>
                <span className="text-[10.5px] tabular-nums text-muted-foreground">{filtered.length}</span>
              </div>

              <div className="relative ms-auto hidden w-56 items-center sm:flex">
                <Search className="pointer-events-none absolute start-2.5 size-3.5 text-muted-foreground" aria-hidden />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Поиск по названию или метке")}
                  aria-label={t("Поиск рассылок")}
                  className="h-7 w-full box-border rounded-btn border border-border bg-background ps-8 pe-7 text-[11.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label={t("Очистить поиск")}
                    className="absolute end-1.5 grid size-4 cursor-pointer place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                ) : null}
              </div>

              <div className="flex items-center gap-0.5 rounded-full border border-border p-1" role="tablist" aria-label={t("Вид списка")}>
                {[
                  { id: "table", label: t("Таблица"), icon: "ri-list-check-2" },
                  { id: "calendar", label: t("Календарь"), icon: "ri-calendar-line" },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={view === t.id}
                    onClick={() => setView(t.id as "table" | "calendar")}
                    className={cn(
                      "inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10.5px] font-semibold transition",
                      view === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={cn(t.icon, "text-[11px] leading-none")} aria-hidden />
                    {t.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setPanel(true)}
                className="inline-flex h-7 flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110"
              >
                <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                {t("Добавить рассылку")}
              </button>

              <div className="relative" ref={menuWrap}>
                <button
                  type="button"
                  onClick={() => setMenu((v) => !v)}
                  aria-label={t("Ещё действия")}
                  aria-expanded={menu}
                  aria-haspopup="menu"
                  className="grid size-7 cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                >
                  <MoreHorizontal className="size-3.5" aria-hidden />
                </button>
                {menu ? (
                  <div role="menu" className="absolute end-0 top-full z-20 mt-1 w-52 rounded-btn border border-border bg-card p-1 shadow-lift">
                    {[
                      { label: t("Шаблоны рассылок"), icon: "ri-layout-grid-line" },
                      { label: t("Импорт рассылок"), icon: "ri-upload-2-line" },
                      { label: t("Экспорт списка"), icon: "ri-download-2-line" },
                    ].map((m) => (
                      <button
                        key={m.label}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenu(false);
                          flash(t("«{label}» в демо-режиме недоступен", { label: m.label }));
                        }}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] text-foreground/80 transition hover:bg-muted hover:text-foreground"
                      >
                        <i className={cn(m.icon, "text-[12px] leading-none text-muted-foreground")} aria-hidden />
                        {m.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            {/* мобильные папки и демо */}
            <div className="flex items-center gap-1.5 overflow-x-auto border-b border-border px-2.5 py-2 lg:hidden">
              {broadcastFolders.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFolder(f.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    folder === f.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                  )}
                >
                  <span className={cn("size-1.5 rounded-full", f.dot)} aria-hidden />
                  {f.label}
                </button>
              ))}
              <button
                type="button"
                role="switch"
                aria-checked={demo}
                aria-label={t("Демо-данные")}
                onClick={toggleDemo}
                className={cn("relative ms-auto h-5 w-9 flex-none cursor-pointer rounded-full border transition", demo ? "border-primary bg-primary" : "border-border bg-foreground/10")}
              >
                <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", demo ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
              </button>
            </div>

            <div className="relative flex min-h-[26rem] flex-col">
              {filtered.length > 0 && view === "table" ? <BroadcastTable items={filtered} onOpen={setDetails} /> : null}
              {filtered.length > 0 && view === "calendar" ? <BroadcastCalendar items={filtered} onOpen={setDetails} /> : null}

              {filtered.length === 0 ? (
                <div className={cn("flex flex-1 flex-col items-center justify-center px-5 py-12 text-center", view === "calendar" && "border-t border-border")}>
                  <div className="relative mx-auto size-32 md:size-36" aria-hidden>
                    <span className="absolute inset-0 rounded-full border border-border/70" />
                    <span className="absolute inset-[19%] rounded-full border border-border/70" />
                    <span className="absolute inset-[38%] rounded-full border border-border/70" />
                    <span className="absolute left-1/2 top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground/40" />
                    <span className="absolute left-1/2 top-1/2 h-[32%] w-[3px] -translate-x-1/2 rounded-full bg-border" />
                  </div>

                  <h5 className="mt-6 max-w-md text-[15px] font-semibold leading-snug">
                    {query
                      ? t("По запросу «{query}» ничего не нашлось", { query })
                      : folder === "all"
                        ? broadcastEmpty.title
                        : t("В папке «{folder}» пока пусто", { folder: folderLabel })}
                  </h5>
                  <p className="mt-1.5 max-w-lg text-[11.5px] leading-relaxed text-muted-foreground">
                    {query ? t("Попробуйте другое название или очистите поиск — рассылки ищутся и по меткам.") : broadcastEmpty.text}
                  </p>

                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    {query ? (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card px-3.5 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                      >
                        {t("Очистить поиск")}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => flash(t("Демо-режим: статья о рассылках в макете недоступна"))}
                          className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border bg-card px-3.5 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                        >
                          {broadcastEmpty.secondary}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPanel(true)}
                          className="inline-flex h-8 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                        >
                          <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                          {broadcastEmpty.primary}
                        </button>
                      </>
                    )}
                  </div>
                  {!demo ? (
                    <button
                      type="button"
                      onClick={toggleDemo}
                      className="mt-4 inline-flex cursor-pointer items-center gap-1 text-[10.5px] font-semibold text-primary underline decoration-dotted underline-offset-2"
                    >
                      <i className="ri-play-circle-line text-[12px] leading-none" aria-hidden />
                      {t("Включить демо-данные и посмотреть, как это выглядит с рассылками")}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {toast ? (
                <div
                  role="status"
                  className="pointer-events-none absolute inset-x-3 bottom-3 z-30 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
                >
                  <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                  {toast}
                </div>
              ) : null}
            </div>

            {/* подсказки */}
            <div className="grid gap-2 border-t border-border p-3.5 sm:grid-cols-3">
              {broadcastHints.map((h) => (
                <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                  <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                    <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                    {h.title}
                  </p>
                  <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                </div>
              ))}
            </div>

            <p className="px-3.5 pb-3.5 text-[11px] text-muted-foreground">
              {all.length > 0
                ? t("Всего рассылок: {total} · получателей в базе: {audience}. Демо-режим: данные живут только на этой странице.", { total: all.length, audience: all.reduce((s, b) => s + b.audience, 0).toLocaleString(locale) })
                : t("Демо-режим: рассылки и календарь живут только на этой странице и не сохраняются после перезагрузки.")}
            </p>
          </div>
        </div>

        {details ? <BroadcastDetails item={details} onClose={() => setDetails(null)} /> : null}

        {panel ? (
          <BroadcastPanel
            onClose={() => setPanel(false)}
            onCreate={create}
            onLearnMore={() => {
              setPanel(false);
              flash(t("Демо-режим: подробная статья о рассылках в макете недоступна"));
            }}
          />
        ) : null}
      </div>
    </Band>
  );
}

export default BroadcastsDemo;